import { useState } from 'react';

import { ChevronDownIcon, TriangleAlertIcon } from 'lucide-react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

import QuickReplies from './QuickReplies';
import SectionSelectors from './SectionSelectors';
import { TeaserHostForm } from './types';

type Props = { pageIndex: number; readOnly?: boolean };

/**
 * The `sections[]` under one page: a different set of actions per part of it.
 *
 * Deliberately **not** numbered, and with no talk of "first match wins" or
 * "priority". Unlike `pages[]`, a section is chosen at runtime by which one
 * holds the largest share of the viewport, so this list's order is
 * presentational. Borrowing the pages editor's wording here would tell the
 * author something untrue.
 *
 * Sections are not added or removed by hand either — the generator proposes the
 * page's parts, and the author edits the actions inside them. The name and the
 * selectors that locate a section are behind a disclosure: they are the escape
 * hatch for when a customer's page changes, not the day-to-day job.
 */
const TeaserSections = ({ pageIndex, readOnly }: Props) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TeaserHostForm>();
  const { fields } = useFieldArray({ control, name: `teaser.pages.${pageIndex}.sections` });
  const sections = useWatch({ control, name: `teaser.pages.${pageIndex}.sections` });

  const [openSection, setOpenSection] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-6">
      {fields.map((field, index) => {
        const isOpen = openSection === field.id;
        const name = sections?.[index]?.id ?? '';
        return (
          <div key={field.id} className="flex flex-col gap-3">
            {index > 0 && <Separator className="mb-3" />}

            <div className="flex items-center gap-2">
              <span className="text-xs leading-4 font-medium text-foreground-muted">
                {name || t('integrations.web_teaser.sections.unnamed')}
              </span>
              {/*
                The generator could only anchor this section by its position, so
                it breaks the next time the page is reordered. Small, because it
                is advice about one section rather than a problem with the page.
              */}
              {sections?.[index]?.fragile && (
                <InfoTooltip content={t('integrations.web_teaser.sections.fragile')}>
                  {/*
                    Labelled, not just tooltipped: a warning a screen reader
                    cannot reach is a warning only some authors get.
                  */}
                  <TriangleAlertIcon
                    size={14}
                    role="img"
                    aria-label={t('integrations.web_teaser.sections.fragile')}
                    className="text-warning"
                  />
                </InfoTooltip>
              )}
              <Button
                type="button"
                variant="ghost-neutral"
                size="icon-sm"
                aria-expanded={isOpen}
                aria-label={t('integrations.web_teaser.sections.advanced')}
                onClick={() => setOpenSection(isOpen ? null : field.id)}
              >
                <ChevronDownIcon
                  size={14}
                  className={cn('transition-transform', isOpen && 'rotate-180')}
                />
              </Button>
            </div>

            {isOpen && (
              <div className="flex flex-col gap-2">
                <Label
                  htmlFor={`teaser-page-${pageIndex}-section-${index}-id`}
                  className="text-sm leading-5 font-medium"
                >
                  {t('integrations.web_teaser.sections.name')}
                </Label>
                {/*
                  There is no separate display name on the wire — the id *is*
                  the name, which is why the generator proposes one from the
                  section's heading.
                */}
                <Controller
                  control={control}
                  name={`teaser.pages.${pageIndex}.sections.${index}.id`}
                  rules={{ required: t('common.required_field') }}
                  render={({ field: nameField }) => (
                    <Input
                      id={`teaser-page-${pageIndex}-section-${index}-id`}
                      placeholder={t('integrations.web_teaser.sections.name_placeholder')}
                      readOnly={readOnly}
                      {...nameField}
                      value={nameField.value ?? ''}
                    />
                  )}
                />
                <SectionSelectors pageIndex={pageIndex} sectionIndex={index} readOnly={readOnly} />
              </div>
            )}

            <QuickReplies
              prefix={`teaser.pages.${pageIndex}.sections.${index}`}
              readOnly={readOnly}
            />
          </div>
        );
      })}
    </div>
  );
};

export default TeaserSections;
